import { useRef, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
} from "react-router-dom";
import CreateSuperAdmin from "./pages/master/CreateSuperAdmin";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import MasterDashboard from "./pages/master/Dashboard";
import Settings from "./pages/master/Settings";
import Login from "./pages/auth/Login";
import SuperAdminDashboard from "./pages/superadmin/Dashboard";
import CustomerList from "./pages/superadmin/customers/CustomerList";
import CreateCustomer from "./pages/superadmin/customers/CreateCustomer";
import CreateProduct from "./pages/superadmin/product/CreateProduct";
import ProductList from "./pages/superadmin/product/ProductList";
import CreateSChallan from "./pages/superadmin/SChallan/CreateSChallan";
import ViewSChallan from "./pages/superadmin/SChallan/ViewSChallan";
import CreatePChallan from "./pages/superadmin/PChallan/CreatePChallan";
import ViewPChallan from "./pages/superadmin/PChallan/ViewPChallan";
import CreateReceipt from "./pages/superadmin/transaction/CreateReceipt";
import ViewReceipt from "./pages/superadmin/transaction/ViewReceipt";
import CreatePayment from "./pages/superadmin/transaction/CreatePayment";
import ViewPayment from "./pages/superadmin/transaction/ViewPayment";
import Ledger from "./pages/superadmin/transaction/Ledger";
import ViewUnits from "./pages/superadmin/unit/ViewUnits";
import Cashbook from "./pages/superadmin/transaction/Cashbook";
import ClosingStockReport from "./pages/superadmin/report/ClosingStockReport";
import SalesReport from "./pages/superadmin/report/SalesReport";
import ProductSalesReport from "./pages/superadmin/report/ProductSalesReport";
import PurchaseReport from "./pages/superadmin/report/PurchaseReport";
import ProductPurchaseReport from "./pages/superadmin/report/ProductPurchaseReport";
import BalanceReport from "./pages/superadmin/report/BalanceReport";
import PriceList from "./pages/superadmin/report/PriceList";
import PrivateRoute from "./components/PrivateRoute";
import CreateOrder from "./pages/superadmin/order/CreateOrder";
import ViewOrder from "./pages/superadmin/order/ViewOrder";
import CreateUser from "./pages/superadmin/user/CreateUser";
import ViewUser from "./pages/superadmin/user/ViewUser";
import ManagerDashboard from "./pages/manager/ManagerDashboard";
import BillingDashboard from "./pages/billing/BillingDashboard";
import BillingCreateOrder from "./pages/billing/order/BillingCreateOrder";
import BillingViewOrder from "./pages/billing/order/BillingViewOrder";
import BillingCreateSChallan from "./pages/billing/SChallan/BillingCreateSChallan";
import BillingCreatePChallan from "./pages/billing/PChallan/BillingCreatePChallan";
import BillingCreatePayment from "./pages/billing/transaction/BillingCreatePayment";
import BillingCreateReceipt from "./pages/billing/transaction/BillingCreateReceipt";
import ManagerCreateProduction from "./pages/manager/production/ManagerCreateProduction";
import ManagerViewProduction from "./pages/manager/production/ManagerViewProduction";
import ManagerCreatePChallan from "./pages/manager/PChallan/ManagerCreatePChallan";
import Attendance from "./pages/manager/Attendance";
import ManagerDailyReport from "./pages/manager/ManagerDailyReport";
import CreateProduction from "./pages/superadmin/production/CreateProduction";
import ViewProduction from "./pages/superadmin/production/ViewProduction";
import ProdCreatePChallan from "./pages/superadmin/production/ProdCreatePChallan";
import SuperAdminAttendance from "./pages/superadmin/attendance/SuperAdminAttendance";

function useIdleLogout() {
  const navigate = useNavigate();
  const timer = useRef();

  const resetTimer = () => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      localStorage.removeItem("user");
      navigate("/");
    }, 10 * 60 * 1000); // 10 minutes
  };

  useEffect(() => {
    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
    ];

    events.forEach((event) => window.addEventListener(event, resetTimer));
    resetTimer(); // start timer on mount

    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer));
      clearTimeout(timer.current);
    };
  }, []);
}

function AppWrapper() {
  useIdleLogout(); // Hook logic moved here
  return (
    <Routes>
      <Route
        path="*"
        element={<div style={{ padding: 50 }}>404 — Not Found</div>}
      />
      <Route
        path="/"
        element={(() => {
          const storedUser = localStorage.getItem("user");
          if (storedUser) {
            const user = JSON.parse(storedUser);
            return (
              <Navigate
                to={
                  user.role === "master"
                    ? "/master/dashboard"
                    : "/super-admin/dashboard"
                }
              />
            );
          } else {
            return <Login />;
          }
        })()}
      />

      <Route
        path="/master/create-super-admin"
        element={
          <ProtectedRoute allowedRoles={["master"]}>
            <CreateSuperAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master/settings"
        element={
          <ProtectedRoute allowedRoles={["master"]}>
            <Settings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/master/dashboard"
        element={
          <ProtectedRoute allowedRoles={["master"]}>
            <MasterDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/super-admin/dashboard"
        element={
          <PrivateRoute>
            <SuperAdminDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/customers"
        element={
          <PrivateRoute>
            <CustomerList />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/customers/create"
        element={
          <PrivateRoute>
            <CreateCustomer />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/product/create"
        element={
          <PrivateRoute>
            <CreateProduct />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/product"
        element={
          <PrivateRoute>
            <ProductList />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/user/create"
        element={
          <PrivateRoute>
            <CreateUser />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/user/view"
        element={
          <PrivateRoute>
            <ViewUser />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/schallan/create"
        element={
          <PrivateRoute>
            <CreateSChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/schallan/view"
        element={
          <PrivateRoute>
            <ViewSChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/pchallan/create"
        element={
          <PrivateRoute>
            <CreatePChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/pchallan/view"
        element={
          <PrivateRoute>
            <ViewPChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/order/create"
        element={
          <PrivateRoute>
            <CreateOrder />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/order/view"
        element={
          <PrivateRoute>
            <ViewOrder />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/production/create"
        element={
          <PrivateRoute>
            <CreateProduction />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/production/view"
        element={
          <PrivateRoute>
            <ViewProduction />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/prodpchallan/create"
        element={
          <PrivateRoute>
            <ProdCreatePChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/attendance"
        element={
          <PrivateRoute>
            <SuperAdminAttendance />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/receipt/create"
        element={
          <PrivateRoute>
            <CreateReceipt />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/receipt/view"
        element={
          <PrivateRoute>
            <ViewReceipt />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/payment/create"
        element={
          <PrivateRoute>
            <CreatePayment />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/payment/view"
        element={
          <PrivateRoute>
            <ViewPayment />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/ledger"
        element={
          <PrivateRoute>
            <Ledger />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/unit/view"
        element={
          <PrivateRoute>
            <ViewUnits />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/cashbook"
        element={
          <PrivateRoute>
            <Cashbook />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/stock-report"
        element={
          <PrivateRoute>
            <ClosingStockReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/sales-report"
        element={
          <PrivateRoute>
            <SalesReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/product-wise-sales-report"
        element={
          <PrivateRoute>
            <ProductSalesReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/purchase-report"
        element={
          <PrivateRoute>
            <PurchaseReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/product-wise-purchase-report"
        element={
          <PrivateRoute>
            <ProductPurchaseReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/balance-report"
        element={
          <PrivateRoute>
            <BalanceReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/super-admin/price-list"
        element={
          <PrivateRoute>
            <PriceList />
          </PrivateRoute>
        }
      />
      {/* <Route
            path="/super-admin/role/create"
            element={
              <PrivateRoute>
                <CreateRole />
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/role/view"
            element={
              <PrivateRoute>
                <ViewRoles />
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/user/create"
            element={
              <PrivateRoute>
                <CreateUser />
              </PrivateRoute>
            }
          />
          <Route
            path="/super-admin/user/view"
            element={
              <PrivateRoute>
                <UserList />
              </PrivateRoute>
            }
          /> */}
      {/* <Route
            path="/super-admin/profit-loss-report"
            element={
              <PrivateRoute>
                <ProfitLossReport />
              </PrivateRoute>
            }
          /> */}
      <Route
        path="/manager/dashboard"
        element={
          <PrivateRoute>
            <ManagerDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/manager/production/create"
        element={
          <PrivateRoute>
            <ManagerCreateProduction />
          </PrivateRoute>
        }
      />
      <Route
        path="/manager/production/create"
        element={
          <PrivateRoute>
            <ManagerCreateProduction />
          </PrivateRoute>
        }
      />
      <Route
        path="/manager/production/view"
        element={
          <PrivateRoute>
            <ManagerViewProduction />
          </PrivateRoute>
        }
      />
      <Route
        path="/manager/pchallan/create"
        element={
          <PrivateRoute>
            <ManagerCreatePChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/manager/attendance"
        element={
          <PrivateRoute>
            <Attendance />
          </PrivateRoute>
        }
      />
      <Route
        path="/manager/attendance/daily"
        element={
          <PrivateRoute>
            <ManagerDailyReport />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/dashboard"
        element={
          <PrivateRoute>
            <BillingDashboard />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/order/create"
        element={
          <PrivateRoute>
            <BillingCreateOrder />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/order/view"
        element={
          <PrivateRoute>
            <BillingViewOrder />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/schallan"
        element={
          <PrivateRoute>
            <BillingCreateSChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/pchallan"
        element={
          <PrivateRoute>
            <BillingCreatePChallan />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/payment"
        element={
          <PrivateRoute>
            <BillingCreatePayment />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing/receipt"
        element={
          <PrivateRoute>
            <BillingCreateReceipt />
          </PrivateRoute>
        }
      />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppWrapper />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
